<template>
    <Card pageTitle="Newsletter Subscribers">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_NEWSLETTER_SUBSCRIBER">
            <div class="subscriber-page">
                <header class="subscriber-header">
                    <div>
                        <p class="eyebrow">Customer engagement</p>
                        <h1>Newsletter subscribers</h1>
                        <p class="intro">
                            Consent-backed website signups stored for future campaigns. Sending is not enabled.
                        </p>
                    </div>
                    <div class="subscriber-total" aria-live="polite">
                        <strong>{{ totalResults }}</strong>
                        <span>active subscriber{{ totalResults === 1 ? "" : "s" }}</span>
                    </div>
                </header>

                <form class="subscriber-search" @submit.prevent="applySearch">
                    <label for="subscriber-search">Search by email</label>
                    <div class="search-row">
                        <input
                            id="subscriber-search"
                            v-model.trim="searchInput"
                            type="search"
                            placeholder="name@example.com"
                            autocomplete="off"
                        />
                        <button type="submit" :disabled="loading">Search</button>
                        <button v-if="search" type="button" class="secondary" :disabled="loading" @click="clearSearch">
                            Clear
                        </button>
                    </div>
                </form>

                <UiState v-if="loading" type="loading" title="Loading newsletter subscribers" compact />
                <UiState
                    v-else-if="loadError"
                    type="error"
                    title="Newsletter subscribers could not be loaded"
                    :message="loadError"
                    action-label="Try again"
                    compact
                    @action="loadSubscribers"
                />
                <UiState
                    v-else-if="subscribers.length === 0"
                    type="empty"
                    :title="search ? 'No matching subscribers' : 'No subscribers yet'"
                    message="New website signups will appear here after capture is enabled."
                    compact
                />
                <div v-else class="subscriber-table-wrap">
                    <table class="subscriber-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Source</th>
                                <th>Consent</th>
                                <th>Subscribed</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="subscriber in subscribers" :key="subscriber.id">
                                <td><a :href="`mailto:${subscriber.email}`">{{ subscriber.email }}</a></td>
                                <td><span class="status-pill">{{ subscriber.status }}</span></td>
                                <td>{{ sourceLabel(subscriber.source) }}</td>
                                <td>{{ subscriber.consentVersion }}</td>
                                <td>{{ formatDate(subscriber.consentedAt) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <footer v-if="!loading && !loadError && totalResults > 0" class="subscriber-footer">
                    <span>Page {{ page }} of {{ Math.max(totalPages, 1) }}</span>
                    <Paginator :totalPages="totalPages" :page="page" @change="changePage" />
                </footer>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import Card from "../../components/UI/Card.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import Paginator from "../../components/other/Paginator.vue"
import UiState from "../../components/UI/UiState.vue"
import NewsletterSubscriberFetchApi from "../../utils/fetch/newsletter-subscriber-fetch-api"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    components: { AllowPermission, Card, Paginator, UiState },
    data() {
        return {
            PERMISSION_CODES,
            subscribers: [],
            loading: true,
            loadError: "",
            searchInput: "",
            search: "",
            page: 1,
            limit: 25,
            totalPages: 0,
            totalResults: 0,
            loadRequestId: 0,
        }
    },
    mounted() {
        this.loadSubscribers()
    },
    methods: {
        async loadSubscribers() {
            const requestId = ++this.loadRequestId
            this.loading = true
            this.loadError = ""
            try {
                const response = await NewsletterSubscriberFetchApi.list({
                    page: this.page,
                    limit: this.limit,
                    status: "active",
                    search: this.search || undefined,
                })
                if (requestId !== this.loadRequestId) return
                this.subscribers = Array.isArray(response.data.result) ? response.data.result : []
                this.totalPages = Number(response.data.totalPages || 0)
                this.totalResults = Number(response.data.totalResults || 0)
            } catch (error) {
                if (requestId !== this.loadRequestId) return
                this.subscribers = []
                this.totalPages = 0
                this.totalResults = 0
                this.loadError = error?.response?.data?.message || "Please try again."
            } finally {
                if (requestId === this.loadRequestId) this.loading = false
            }
        },
        applySearch() {
            this.search = this.searchInput
            this.page = 1
            this.loadSubscribers()
        },
        clearSearch() {
            this.searchInput = ""
            this.search = ""
            this.page = 1
            this.loadSubscribers()
        },
        changePage(page) {
            if (page === this.page || page < 1 || page > this.totalPages) return
            this.page = page
            this.loadSubscribers()
        },
        sourceLabel(source) {
            return source === "wordpress_import" ? "WordPress import" : "Website"
        },
        formatDate(value) {
            if (!value) return "—"
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return "—"
            return new Intl.DateTimeFormat("en-CA", {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(date)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.subscriber-page { display: grid; gap: 24px; }
.subscriber-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
.eyebrow { margin: 0 0 6px; color: $primaryColor; font-size: 12px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
h1 { margin: 0; color: $primaryColor; font-size: 30px; }
.intro { max-width: 680px; margin: 8px 0 0; color: rgba($textColor, .72); }
.subscriber-total { min-width: 180px; padding: 16px 20px; border: 1px solid $lightBorderColor; border-radius: 10px; background: rgba($primaryColor, .04); }
.subscriber-total strong, .subscriber-total span { display: block; }
.subscriber-total strong { color: $primaryColor; font-size: 28px; }
.subscriber-total span { color: rgba($textColor, .72); font-size: 13px; }
.subscriber-search label { display: block; margin-bottom: 7px; color: $textColor; font-size: 13px; font-weight: 700; }
.search-row { display: flex; gap: 10px; }
.search-row input { width: min(480px, 100%); min-height: 42px; padding: 9px 13px; border: 1px solid $lightBorderColor; border-radius: 7px; }
.search-row button { min-height: 42px; padding: 9px 18px; border: 1px solid $primaryColor; border-radius: 7px; background: $primaryColor; color: white; font-weight: 700; }
.search-row button.secondary { background: transparent; color: $primaryColor; }
.search-row button:disabled { cursor: wait; opacity: .55; }
.subscriber-table-wrap { overflow-x: auto; border: 1px solid $lightBorderColor; border-radius: 10px; }
.subscriber-table { width: 100%; border-collapse: collapse; }
.subscriber-table th, .subscriber-table td { padding: 14px 16px; border-bottom: 1px solid $lightBorderColor; text-align: left; white-space: nowrap; }
.subscriber-table th { background: rgba($primaryColor, .04); color: $primaryColor; font-size: 12px; letter-spacing: .04em; text-transform: uppercase; }
.subscriber-table tbody tr:last-child td { border-bottom: 0; }
.subscriber-table a { color: $primaryColor; font-weight: 700; }
.status-pill { display: inline-flex; padding: 4px 9px; border-radius: 999px; background: rgba(31, 132, 87, .12); color: #176b49; font-size: 12px; font-weight: 800; text-transform: capitalize; }
.subscriber-footer { display: flex; align-items: center; justify-content: space-between; gap: 20px; color: rgba($textColor, .7); font-size: 13px; }

@media (max-width: 760px) {
    .subscriber-header, .subscriber-footer { align-items: stretch; flex-direction: column; }
    .subscriber-total { min-width: 0; }
    .search-row { flex-wrap: wrap; }
}
</style>
